import { IKeyValuePair } from "@app/interfaces/IKeyValuePair";
import { AccountUserType } from "@enums/UserType";
import { IRunBlockRootScope } from "@interfaces/IRunBlockRootScope";
import { IAccountUserMapping, IUser, UserPlatform } from "@interfaces/IUser";
import { IReportDefinition } from "@reports/models/IReportDefinition";
import { AccountFeatures } from "../enum/account/AccountFeature";
import { SpecialUserPermissions } from "../enum/SpecialUserPermissions";
import { AccountService, IAccount } from "./account.service";
import { EnumService } from "./enum.service";
import { TranslationService } from "./translation.service";

export interface IAccountFeatureDescriptionMap {
    [key: number]: IAccountFeatureDescription
}

export interface IAccountFeatureDescription {
    name: string;
    description: string;
    enabled: boolean;
    feature: AccountFeatures;
    hasManageFunctionality: boolean; // used to determine whether or not we need both Read & Manage toggles
}

export interface ISpecialUserPermissionDescription {
    name: string;
    description: string;
    permission: SpecialUserPermissions;
}

export interface ISpecialUserPermissionDescriptionMap {
    [key: number]: ISpecialUserPermissionDescription
}

export interface IUserRoleMap {
    [key: string]: boolean
}

export enum FeatureAccessType {
    Read,
    Manage
}

export class UserRole {
    static Administrator: string = "administrator";
    static History: string = "history";
    static ManageDrivers: string = "managedrivers";
    static ManageVehicles: string = "managevehicles";
    static ManageGeofences: string = "managegeofences";
    static ManageGroups: string = "managegroups";
    static ManageAlerts: string = "managealerts";
    static ManageReportBuilder: string = "managereportbuilder";
    static ManageAlertReviews: string = "managealertreviews";
    static ShowReports: string = "showreports";
}

export class AccountFeatureService {

    featureStringCache: string[] = [];

    static $inject = ["$rootScope", "accountService", "translationService"];

    constructor(
        private readonly $rootScope: IRunBlockRootScope,
        private readonly accountService: AccountService,
        private readonly translationService: TranslationService
    ) {
    }

    currentUserHasFeatureAccess(feature: AccountFeatures, type: FeatureAccessType = FeatureAccessType.Read, skipAccountFeatureCheck: boolean = false): boolean {
        const accountData = this.accountService.getAccountDataFromCache();

        return this.accountAndUserHaveFeatureAccess(accountData, this.$rootScope.currentUser, feature, type, skipAccountFeatureCheck);
    }

    currentUserHasAnyFeatureAccess(features: AccountFeatures[], type: FeatureAccessType = FeatureAccessType.Read, skipAccountFeatureCheck: boolean = false): boolean {
        const accountData = this.accountService.getAccountDataFromCache();

        return this.accountAndUserHaveAnyFeatureAccess(accountData, this.$rootScope.currentUser, features, type, skipAccountFeatureCheck);
    }

    currentUserHasAllFeatureAccess(features: AccountFeatures[], type: FeatureAccessType = FeatureAccessType.Read, skipAccountFeatureCheck: boolean = false): boolean {
        const accountData = this.accountService.getAccountDataFromCache();

        return this.accountAndUserHaveAllFeatureAccess(accountData, this.$rootScope.currentUser, features, type, skipAccountFeatureCheck);
    }

    accountHasFeatureAccess(accountData: IAccount, feature: AccountFeatures): boolean {
        return (accountData.Features & feature) === feature;
    }

    featureFlagsHaveFeatureAccess(features: AccountFeatures, feature: AccountFeatures): boolean {
        return (features & feature) === feature;
    }

    accountHasAllFeatureAccess(accountData: IAccount, features: AccountFeatures[]): boolean {
        if (!accountData || !features?.length) {
            return false;
        }

        let hasAllFeatures = true;

        for (let i = 0; i < features.length; i++) {
            if (this.accountHasFeatureAccess(accountData, features[i])) {
                continue;
            }

            hasAllFeatures = false;
            break;
        }

        return hasAllFeatures;
    }

    accountHasAnyFeatureAccess(accountData: IAccount, features: AccountFeatures[]): boolean {
        if (!accountData || !features?.length) {
            return false;
        }

        let isAnyEnabled = false;

        for (let i = 0; i < features.length; i++) {
            if (!this.accountHasFeatureAccess(accountData, features[i])) {
                continue;
            }

            isAnyEnabled = true;
            break;
        }

        return isAnyEnabled;
    }

    accountAndUserHaveFeatureAccess(accountData: IAccount, user: IUser, feature: AccountFeatures, type: FeatureAccessType = FeatureAccessType.Read, skipAccountFeatureCheck: boolean = false): boolean {
        const accountHasAccess = this.accountHasFeatureAccess(accountData, feature);

        if (!skipAccountFeatureCheck && !accountHasAccess) {
            return false;
        }

        if (!this.hasBasePermissionModel(user)) {
            return false;
        }

        const hasUserManageAccess = (user.CurrentAccountUserMapping.ManageFeatures & feature) === feature;
        let hasUserReadAccess = (user.CurrentAccountUserMapping.ReadFeatures & feature) === feature;

        // overwriting the CurrentAccountUserMapping setting as we don't add toggles for these features on user-editor, taking account feature flag instead
        if ([AccountFeatures.Can, AccountFeatures.GPS, AccountFeatures.FaultCodes, AccountFeatures.Tyres].includes(feature)) {
            hasUserReadAccess = this.accountHasFeatureAccess(accountData, feature);
        }

        if (this.isAdmin(user)) {
            return true;
        }

        if (!hasUserReadAccess) {
            return false;
        }

        if (type === FeatureAccessType.Manage) {
            return hasUserManageAccess;
        }

        return hasUserReadAccess;
    }

    accountAndUserHaveAnyFeatureAccess(accountData: IAccount, user: IUser, features: AccountFeatures[], type: FeatureAccessType = FeatureAccessType.Read, skipAccountFeatureCheck: boolean = false): boolean {
        if (!skipAccountFeatureCheck && !accountData) {
            return false;
        }

        if (!user || !features?.length) {
            return false;
        }

        let isAnyEnabled = false;

        for (let i = 0; i < features.length; i++) {
            if (!this.accountAndUserHaveFeatureAccess(accountData, user, features[i], type, skipAccountFeatureCheck)) {
                continue;
            }

            isAnyEnabled = true;
            break;
        }

        return isAnyEnabled;
    }

    accountAndUserHaveAllFeatureAccess(accountData: IAccount, user: IUser, features: AccountFeatures[], type: FeatureAccessType = FeatureAccessType.Read, skipAccountFeatureCheck: boolean = false): boolean {
        if (!skipAccountFeatureCheck && !accountData) {
            return false;
        }

        if (!user || !features?.length) {
            return false;
        }

        let hasAllFeatures = true;

        for (let i = 0; i < features.length; i++) {
            if (this.accountAndUserHaveFeatureAccess(accountData, user, features[i], type, skipAccountFeatureCheck)) {
                continue;
            }

            hasAllFeatures = false;
            break;
        }

        return hasAllFeatures;
    }

    enableUserMappingFeatureAccess(mapping: IAccountUserMapping, feature: AccountFeatures, type: FeatureAccessType = FeatureAccessType.Read): void {
        if (type === FeatureAccessType.Manage) {
            mapping.ManageFeatures = mapping.ManageFeatures | feature;
        }

        if (type === FeatureAccessType.Read) {
            mapping.ReadFeatures = mapping.ReadFeatures | feature;
        }
    }

    disableUserMappingFeatureAccount(mapping: IAccountUserMapping, feature: AccountFeatures, type: FeatureAccessType = FeatureAccessType.Read): void {
        if (type === FeatureAccessType.Manage) {
            mapping.ManageFeatures = mapping.ManageFeatures & ~feature;
        }

        if (type === FeatureAccessType.Read) {
            mapping.ReadFeatures = mapping.ReadFeatures & ~feature;
        }
    }

    enableAccountFeature(accountData: IAccount, feature: AccountFeatures): void {
        accountData.Features = accountData.Features | feature;
    }

    disableAccountFeature(accountData: IAccount, feature: AccountFeatures): void {
        accountData.Features = accountData.Features & ~feature;
    }

    toggleAccountFeature(accountData: IAccount, feature: AccountFeatures): void {
        const currentlyEnabled = this.accountHasFeatureAccess(accountData, feature);

        if (currentlyEnabled) {
            this.disableAccountFeature(accountData, feature);
            return;
        }

        this.enableAccountFeature(accountData, feature);
    }

    currentUserHasRole(rolesToCheck: string[], allRoles = true): boolean {
        if (!rolesToCheck?.length) {
            return false;
        }

        const user = this.$rootScope.currentUser;

        // If we dont have a user OR we are an internal user who has not set our account mappings, then we cannot access these routes
        if (!user || user.platform == UserPlatform.Foundation || !user.CurrentAccountUserMapping) {
            return false;
        }

        if (!user || user.CurrentAccountUserMapping.Type == AccountUserType.Admin) {
            return true;
        }

        const rolesUserHas: IUserRoleMap = {
            [UserRole.Administrator]: user.CurrentAccountUserMapping.Type < 1,
            [UserRole.History]: this.hasSpecialUserPermission(user.CurrentAccountUserMapping, SpecialUserPermissions.ShowHistoricalPlots),
            [UserRole.ManageAlerts]: this.hasSpecialUserPermission(user.CurrentAccountUserMapping, SpecialUserPermissions.ManageAlerts),
            [UserRole.ManageDrivers]: this.hasSpecialUserPermission(user.CurrentAccountUserMapping, SpecialUserPermissions.ManageDrivers),
            [UserRole.ManageGeofences]: this.hasSpecialUserPermission(user.CurrentAccountUserMapping, SpecialUserPermissions.ManageGeofences),
            [UserRole.ManageGroups]: this.hasSpecialUserPermission(user.CurrentAccountUserMapping, SpecialUserPermissions.ManageGroups),
            [UserRole.ManageVehicles]: this.hasSpecialUserPermission(user.CurrentAccountUserMapping, SpecialUserPermissions.ManageVehicles),
            [UserRole.ManageReportBuilder]: this.hasSpecialUserPermission(user.CurrentAccountUserMapping, SpecialUserPermissions.ManageReportBuilder),
            [UserRole.ManageAlertReviews]: this.hasSpecialUserPermission(user.CurrentAccountUserMapping, SpecialUserPermissions.ManageAlertReviews),
            [UserRole.ShowReports]: this.hasSpecialUserPermission(user.CurrentAccountUserMapping, SpecialUserPermissions.ShowReports),
        }

        let userHasAllRoles = true;
        let userHasRoles = false;

        rolesToCheck.forEach(role => {
            role = role.toLowerCase().trim();
            const userInRole = rolesUserHas[role];

            userHasAllRoles = userHasAllRoles && userInRole;
            userHasRoles = userHasRoles || userInRole;
        });

        if (allRoles) {
            return userHasAllRoles;
        }

        return userHasRoles;
    }

    doesCurrentUserHaveSpecialPermission(permission: SpecialUserPermissions): boolean {
        const user = this.$rootScope.currentUser;

        if (!this.hasBasePermissionModel(user)) {
            return false;
        }

        if (this.isAdmin(user)) {
            return true;
        }

        return this.hasSpecialUserPermission(user.CurrentAccountUserMapping, permission);
    }

    hasSpecialUserPermission(accountUserMapping: IAccountUserMapping, permission: SpecialUserPermissions): boolean {
        if (!accountUserMapping.SpecialUserPermissions || !SpecialUserPermissions[permission]) {
            return false;
        }

        return (accountUserMapping.SpecialUserPermissions & permission) === permission;
    }

    getSpecialUserPermissionDescription(permission: SpecialUserPermissions): ISpecialUserPermissionDescription {
        const descriptions = this.getSpecialUserPermissionDescriptions();

        return descriptions.find(description => description.permission === permission);
    }

    getSpecialUserPermissionDescriptions(): ISpecialUserPermissionDescription[] {
        const descriptions: ISpecialUserPermissionDescription[] = [];

        this.getSpecialUserPermissionsEnumKeys().forEach(key => {
            descriptions.push({
                permission: SpecialUserPermissions[key],
                name: this.translationService.t(`SpecialUserPermissionsEnum.${key}`),
                description: this.translationService.t(`SpecialUserPermissionDescription.${key}`)
            });
        });

        descriptions.sort((description1, description2) => description1.name.localeCompare(description2.name));

        return descriptions;
    }

    getSpecialUserPermissionsDescriptionsMap(): ISpecialUserPermissionDescriptionMap {
        const descriptionsMap: ISpecialUserPermissionDescriptionMap = {};
        const descriptions = this.getSpecialUserPermissionDescriptions();

        descriptions.forEach(description => descriptionsMap[description.permission] = description);

        return descriptionsMap;
    }

    getFeatureDescriptionsForAccount(accountData: IAccount): IAccountFeatureDescription[] {
        return this.getFeatureDescriptions(accountData);
    }

    getFeatureDescriptionsForAccountAndUser(accountData: IAccount, user: IUser): IAccountFeatureDescription[] {
        if (!this.hasBasePermissionModel(user)) return [];

        return this.getFeatureDescriptions(accountData, user, true);
    }

    getFeatureDescriptionsMapForAccount(accountData: IAccount): IAccountFeatureDescriptionMap {
        return this.getFeatureDescriptionsMap(accountData);
    }

    getFeatureDescriptionsMapForAccountAndUser(accountData: IAccount, user: IUser): IAccountFeatureDescriptionMap {
        if (!this.hasBasePermissionModel(user)) return {};

        return this.getFeatureDescriptionsMap(accountData, user, true);
    }

    createFeatureDescription(enumKey: string, enabled: boolean = false): IAccountFeatureDescription {
        // Skip as we dont want to "say" default
        if (AccountFeatures[enumKey] === AccountFeatures.Default) {
            return;
        }

        // Skip GPS as its confusing
        if (AccountFeatures[enumKey] === AccountFeatures.GPS) {
            return;
        }

        // Ignoring these for now
        if ([AccountFeatures.Can, AccountFeatures.FaultCodes, AccountFeatures.Tyres].includes(AccountFeatures[enumKey])) {
            return;
        }

        let name = this.translationService.tEnum(AccountFeatures, "AccountFeatures", AccountFeatures[enumKey]);
        let description = this.translationService.t(`AccountFeaturesDescription.${enumKey}`);

        // Added to support the use of Planning when TMS is toggled off on an account
        if (AccountFeatures[enumKey] === AccountFeatures.TMS && !enabled) {
            name = this.translationService.t("Common.Planning");
            description = this.translationService.t(`AccountFeaturesDescription.Planning`);
        }

        return {
            name: name,
            description: description,
            feature: AccountFeatures[enumKey],
            enabled: enabled,
            hasManageFunctionality: this.hasManageFunctionality(AccountFeatures[enumKey])
        }
    }

    getDescriptionsForFeaturesFlag(accountFeatures: AccountFeatures): IAccountFeatureDescription[] {
        const features: IAccountFeatureDescription[] = [];

        this.getAccountFeaturesEnumKeys().forEach(enumKey => {

            const enabled = this.featureFlagsHaveFeatureAccess(accountFeatures, AccountFeatures[enumKey]);
            const newFeatureDescription = this.createFeatureDescription(enumKey, enabled);

            if (!newFeatureDescription) {
                return;
            }

            features.push(newFeatureDescription);
        });

        features.sort((feature1, feature2) => feature1.name.localeCompare(feature2.name));

        return features;
    }

    printAccountFeatureListForAccount(accountData: IAccount): void {
        const featureList: IAccountFeatureDescription[] = this.getFeatureDescriptions(accountData);

        featureList.forEach(feature => {
            const icon = feature.enabled ? "✅" : "🚫";

            console.log(`${icon} ${feature.name}`);
        });
    }

    getFeatureString(features: AccountFeatures): string {
        if (this.featureStringCache[features]) {
            return this.featureStringCache[features];
        }

        const featureString = this.getDescriptionsForFeaturesFlag(features).reduce((x, y) => {
            if (y.enabled) {
                return x + (x ? ", " : "") + y.name;
            }

            return x;
        }, "");

        this.featureStringCache[features] = featureString;

        return featureString;
    }

    reportTypeMappings = [
        { reportType: "Camera", accountFeature: [AccountFeatures.Camera] },
        { reportType: "VideoStreaming", accountFeature: [AccountFeatures.Camera] },
        { reportType: "Tacho", accountFeature: [AccountFeatures.Tacho] },
        { reportType: "Temperature", accountFeature: [AccountFeatures.Temperature] },
        { reportType: "TemperatureOutOfBounds", accountFeature: [AccountFeatures.Temperature] },
        { reportType: "AlcolockReadings", accountFeature: [AccountFeatures.Breathalock] },
        { reportType: "Alcolock", accountFeature: [AccountFeatures.Breathalock] },
        { reportType: "Journey", accountFeature: [AccountFeatures.TrackingDevice, AccountFeatures.Can, AccountFeatures.Camera] },
        { reportType: "StationaryLocation", accountFeature: [AccountFeatures.TrackingDevice, AccountFeatures.Can, AccountFeatures.Camera] },
        { reportType: "GeofenceVisit", accountFeature: [AccountFeatures.TrackingDevice, AccountFeatures.Can, AccountFeatures.Camera] },
        { reportType: "GeofenceVisits", accountFeature: [AccountFeatures.TrackingDevice, AccountFeatures.Can, AccountFeatures.Camera] },
        { reportType: "TMS", accountFeature: [AccountFeatures.TrackingDevice, AccountFeatures.Can] },
        { reportType: "VehicleInput", accountFeature: [AccountFeatures.TrackingDevice, AccountFeatures.Can] },
    ]

    isReportFeatureEnabledOnAccount(accountData: IAccount, reportDefinition: IReportDefinition): boolean {
        if (reportDefinition.TableDefinition.ReportFeatures == undefined || reportDefinition.TableDefinition.ReportFeatures == 0) {
            return true;
        }

        return (accountData.Features & reportDefinition.TableDefinition.ReportFeatures) > 0;
    }

    isAnyReportTypeEnabled(accountData: IAccount, reportTypes: string): boolean {
        let enabled = true;

        reportTypes.split(",").forEach(reportType => {
            enabled = this.isReportTypeEnabled(accountData, reportType);
        });

        return enabled;
    }

    isReportTypeEnabled(accountData: IAccount, reportType: string): boolean {
        let enabled = true;

        this.reportTypeMappings.forEach(mapping => {
            const currentUser = this.$rootScope.currentUser;

            if (mapping.reportType === reportType.replace(/\s/g, '') && !this.accountAndUserHaveAnyFeatureAccess(accountData, currentUser, mapping.accountFeature)) {
                enabled = false;
            }
        });

        return enabled;
    }

    findFeaturesFromFeaturesEnum(value: number): AccountFeatures[] {
        const features: IKeyValuePair<typeof AccountFeatures>[] = EnumService.enumToArray("AccountFeatures", AccountFeatures);
        const includedFeatures: AccountFeatures[] = [];

        if (!features.length) {
            return includedFeatures;
        }

        features.sort((feature1: IKeyValuePair<typeof AccountFeatures>, feature2: IKeyValuePair<typeof AccountFeatures>) => {
            return (+feature2.value) - (+feature1.value)
        });

        features.forEach((feature, index) => {
            const enumValue: number = +feature.value;

            if (value == enumValue || value / enumValue >= 1) {
                includedFeatures.push(enumValue);
                value -= enumValue;
            }
        });

        return includedFeatures;
    }

    getIconForFeature(feature: AccountFeatures) {
        switch (feature) {
            case AccountFeatures.Camera:
                return `videocam`;

            case AccountFeatures.GPS:
                return `near_me`;

            default:
                return `token`;
        }
    }

    // ---

    private hasBasePermissionModel(user: IUser): boolean {
        return (user && user.platform !== UserPlatform.Foundation && !!user.CurrentAccountUserMapping);
    }

    private isAdmin(user: IUser): boolean {
        return user?.CurrentAccountUserMapping?.Type == AccountUserType.Admin;
    }

    private getFeatureDescriptionsMap(accountData: IAccount, user: IUser = null, requireUser: boolean = false): IAccountFeatureDescriptionMap {
        const descriptionsMap: IAccountFeatureDescriptionMap = {};
        const descriptions = (requireUser)
            ? this.getFeatureDescriptions(accountData, user, requireUser)
            : this.getFeatureDescriptions(accountData);

        descriptions.forEach(description => descriptionsMap[description.feature] = description);

        return descriptionsMap;
    }

    private getFeatureDescriptions(accountData: IAccount, user: IUser = null, requireUser: boolean = false): IAccountFeatureDescription[] {
        const features: IAccountFeatureDescription[] = [];

        this.getAccountFeaturesEnumKeys().forEach(enumKey => {

            const enabled = (requireUser)
                ? this.accountAndUserHaveFeatureAccess(accountData, user, AccountFeatures[enumKey])
                : this.accountHasFeatureAccess(accountData, AccountFeatures[enumKey]);

            const newFeatureDescription = this.createFeatureDescription(enumKey, enabled);

            if (!newFeatureDescription) {
                return;
            }

            features.push(newFeatureDescription);
        });

        features.sort((feature1, feature2) => feature1.name.localeCompare(feature2.name));

        return features;
    }

    private getSpecialUserPermissionsEnumKeys(): string[] {
        return Object.keys(SpecialUserPermissions).filter(key => !isNaN(Number(SpecialUserPermissions[key])) && SpecialUserPermissions[key] > 0);
    }

    private getAccountFeaturesEnumKeys(): string[] {
        return Object.keys(AccountFeatures).filter(key => !isNaN(Number(AccountFeatures[key])) && AccountFeatures[key] > 0);
    }

    private hasManageFunctionality(feature: AccountFeatures): boolean {
        const featuresWithManageFunctionality: AccountFeatures[] = [
            AccountFeatures.TMS
        ];

        return featuresWithManageFunctionality.includes(feature);
    }
}
